import type { SectionElementLayoutTypes } from '../../types';

export interface BaseElement {
	tag: string;
	attributes?: Record<string, string | undefined>;
	children?: Array<ParsedElement> | string;
}

// Section element with layout attributes
export interface SectionElement extends BaseElement {
	tag: 'section' | string;
	attributes?: {
		n: string;
		layout: SectionElementLayoutTypes;
		folded: '1';
		[key: string]: string;
	};
}

// Markdown content element
export interface MarkdownElement extends BaseElement {
	tag: 'md';
	attributes?: {
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		[key: string]: string | undefined;
	};
}

// Header element
export interface HeaderElement extends BaseElement {
	tag: 'h';
	attributes?: {
		level: '1' | '2' | '3' | '4' | '5' | '6' | string;
		header: string;
		[key: string]: string;
	};
}

export function isHeaderElement(element: ParsedElement): element is HeaderElement {
	return typeof element !== 'string' && element.tag === 'h';
}

// Carousel element for displaying multiple items
export interface CarouselElement extends BaseElement {
	tag: 'carousel';
	attributes?: {
		title: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		[key: string]: string | undefined;
	};
}

// Smart link element
export interface SmartLinkElement extends BaseElement {
	tag: 'smart-link';
	attributes?: {
		url?: string;
		title?: string;
		type?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
}

// Action element
export interface ActionElement extends BaseElement {
	tag: 'action-card';
	attributes?: {
		title: string;
		text?: string;
		url?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
}

// Action element
export interface ProjectHighlightElement extends BaseElement {
	tag: 'project-highlight-card';
	attributes?: {
		title: string;
		content?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
}

// Contact element
export interface ContactElement extends BaseElement {
	tag: 'contact-card';
	attributes?: {
		name?: string;
		message?: string;
		avatar_url?: string;
		job_title?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
}

// People element
export interface PersonElement extends BaseElement {
	tag: 'people-card';
	attributes?: {
		url?: string;
		name?: string;
		job_title?: string;
		location?: string;
		account_id?: string;
		email?: string;
		avatar_url?: string;
		[key: string]: string | undefined;
	};
}

// Reporting lines element
export interface ReportingLinesElement extends BaseElement {
	tag: 'report-lines';
	attributes?: {
		id?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
}

// List element
export interface ListElement extends BaseElement {
	tag: 'ul' | 'ol' | 'list';
	attributes?: {
		type?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		[key: string]: string | undefined;
	};
}

// List item element
export interface ListItemElement extends BaseElement {
	tag: 'li';
	attributes?: {
		[key: string]: string | undefined;
	};
}

// Jira epic progress element (epics, epic issues)
export interface JiraEpicProgressElement extends BaseElement {
	tag: 'jira-epic-progress';
	attributes?: {
		id?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
}

// Jira project burndown chart element
export interface JiraBurndownChartElement extends BaseElement {
	tag: 'jira-burndown-chart';
	attributes?: {
		id?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
}

// Jira Work Distribution element
export interface JiraWorkDistributionElement extends BaseElement {
	tag: 'jira-work-distribution';
	attributes?: {
		id?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
}

// Union type of all possible parsed elements
export type ParsedElement =
	| SectionElement
	| ListElement
	| MarkdownElement
	| HeaderElement
	| CarouselElement
	| SmartLinkElement
	| ActionElement
	| PersonElement
	| ProjectHighlightElement
	| ContactElement
	| ReportingLinesElement
	| JiraEpicProgressElement
	| JiraWorkDistributionElement
	| JiraBurndownChartElement
	| string;

export type ParsedLayout = SectionElement[];
